import React from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { PlayIcon, PauseIcon, RotateCcwIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, UserCheckIcon } from 'lucide-react';
import { CavallaMark } from '../brand/Logo';
const EASE = [0.23, 1, 0.32, 1] as const;
const SCATTER = [{
  x: 6,
  y: 12
}, {
  x: 30,
  y: 4
}, {
  x: 62,
  y: 16
}, {
  x: 84,
  y: 6
}, {
  x: 14,
  y: 52
}, {
  x: 44,
  y: 40
}, {
  x: 72,
  y: 58
}, {
  x: 90,
  y: 44
}, {
  x: 24,
  y: 82
}, {
  x: 56,
  y: 76
}, {
  x: 80,
  y: 88
}, {
  x: 4,
  y: 70
}];
const GRID = SCATTER.map((_, index) => ({
  x: 26 + index % 4 * 16,
  y: 22 + Math.floor(index / 4) * 26
}));
function Fragments({
  ordered
}: {
  ordered: boolean;
}) {
  const reduced = useReducedMotion();
  return <div className="relative h-full w-full" data-id="element-250" data-mp="15jgsxeih8k3v">
      {SCATTER.map((start, index) => {
      const target = ordered ? GRID[index] : start;
      return <motion.span key={index} className={`absolute h-3.5 w-3.5 ${ordered ? 'bg-gold' : 'bg-white/35'}`} style={{
        left: `${target.x}%`,
        top: `${target.y}%`
      }} initial={reduced ? false : {
        left: `${start.x}%`,
        top: `${start.y}%`,
        opacity: 0.5
      }} animate={{
        left: `${target.x}%`,
        top: `${target.y}%`,
        opacity: 1
      }} transition={{
        duration: 0.9,
        ease: EASE,
        delay: index * 0.05
      }} data-id="element-251" data-mp="10lyh9gd2rqs6" data-mpi={index} />;
    })}
      {ordered && <motion.div className="absolute inset-x-[22%] inset-y-[16%] border border-gold/40" initial={reduced ? false : {
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4,
      ease: EASE,
      delay: 0.7
    }} data-id="element-252" data-mp="30ofpsov51ua7" />}
    </div>;
}
function ApplyVisual() {
  const reduced = useReducedMotion();
  const bars = [72, 48, 90, 61];
  return <div className="grid h-full w-full grid-cols-2 gap-5" data-id="element-253" data-mp="1k7tjhhl51v6b">
      <div className="border border-white/20 p-5" data-id="element-254" data-mp="1k8bob2lv9hpu">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55" data-id="element-255" data-mp="2ni7f66orxkr7">
          Machine analysis
        </p>
        <div className="mt-5 space-y-3" data-id="element-256" data-mp="2ikxsuzwolp7l">
          {bars.map((width, index) => <div key={index} className="h-2 w-full bg-white/12" data-id="element-257" data-mp="2wjnosc7rliud" data-mpi={index}>
              <motion.span className="block h-full bg-[#8FA9CE]" initial={reduced ? false : {
            width: 0
          }} animate={{
            width: `${width}%`
          }} transition={{
            duration: 0.7,
            ease: EASE,
            delay: 0.15 + index * 0.1
          }} data-id="element-258" data-mp="2shttykk93924" />
            </div>)}
        </div>
      </div>
      <div className="border border-white/20 p-5" data-id="element-259" data-mp="3g6d475mv1vwu">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55" data-id="element-260" data-mp="3b8biecy1viho">
          Human judgement
        </p>
        <ul className="mt-5 space-y-3" data-id="element-261" data-mp="3p7b8zq54i7gh">
          {['Definition agreed', 'Outlier challenged', 'Confidence stated'].map((item, index) => <motion.li key={item} className="flex items-center gap-2.5 text-[13px] text-white/80" initial={reduced ? false : {
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.35,
          ease: EASE,
          delay: 0.3 + index * 0.14
        }} data-id="element-262" data-mp="3kat5supfyh69" data-mpi={index}>
              <CheckIcon className="h-3.5 w-3.5 shrink-0 text-gold" aria-hidden="true" data-id="element-263" data-mp="2q126bz4utz4" />
              {item}
            </motion.li>)}
        </ul>
      </div>
      <motion.div className="col-span-2 flex flex-wrap items-center justify-between gap-4 border-l-2 border-gold bg-white/8 px-5 py-4" initial={reduced ? false : {
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: EASE,
      delay: 0.8
    }} data-id="element-264" data-mp="3l27cdop0ejax">
        <p className="font-display text-[18px] text-white" data-id="element-265" data-mp="3g2uor1qbd87a">A finding you can defend</p>
        <p className="text-[13px] text-white/60" data-id="element-266" data-mp="327ivyzvpxrt0">Confidence stated at 86 per cent</p>
      </motion.div>
    </div>;
}
function EnableVisual() {
  const reduced = useReducedMotion();
  const handovers = [{
    title: 'The models',
    detail: 'Documented and yours to run'
  }, {
    title: 'The dashboards',
    detail: 'Live, with agreed definitions'
  }, {
    title: 'The training',
    detail: 'Role specific, repeated'
  }];
  return <div className="flex h-full w-full flex-col justify-center gap-4" data-id="element-267" data-mp="2lcog9tlbn0b9">
      {handovers.map((item, index) => <motion.div key={item.title} className="flex items-center justify-between border border-white/20 px-5 py-4" initial={reduced ? false : {
      opacity: 0,
      x: -14
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.45,
      ease: EASE,
      delay: index * 0.16
    }} data-id="element-268" data-mp="2gdv2e024h7ak" data-mpi={index}>
          <span className="font-display text-[19px] text-white" data-id="element-269" data-mp="22evp1ok28etp">{item.title}</span>
          <span className="flex items-center gap-3 text-[13px] text-white/60" data-id="element-270" data-mp="4o2jm9q51mmy">
            {item.detail}
            <ChevronRightIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-271" data-mp="n8ujd0ym1sh" />
          </span>
        </motion.div>)}
      <motion.p className="mt-2 text-[13px] uppercase tracking-[0.18em] text-white/45" initial={reduced ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4,
      delay: 0.7
    }} data-id="element-272" data-mp="1ijattucztauj">
        Handed to your team
      </motion.p>
    </div>;
}
function DecideVisual() {
  const reduced = useReducedMotion();
  const options = [{
    label: 'Enter all three markets',
    note: 'Capital exposure too high'
  }, {
    label: 'Enter two, sequenced',
    note: 'Recommended',
    chosen: true
  }, {
    label: 'Hold for a further quarter',
    note: 'Cost of delay exceeds gain'
  }];
  return <div className="flex h-full w-full flex-col justify-center gap-3" data-id="element-273" data-mp="18nzeb9trfzlq">
      {options.map((option, index) => <motion.div key={option.label} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${option.chosen ? 'border-2 border-gold bg-gold/8' : 'border border-white/18'}`} initial={reduced ? false : {
      opacity: 0,
      y: 8
    }} animate={{
      opacity: option.chosen ? 1 : 0.65,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: EASE,
      delay: index * 0.14
    }} data-id="element-274" data-mp="2k11va5zrur6f" data-mpi={index}>
          <span className={`text-[16px] ${option.chosen ? 'text-white' : 'text-white/70'}`} data-id="element-275" data-mp="yui1u0z969kt">
            {option.label}
          </span>
          <span className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-white/55" data-id="element-276" data-mp="tux0xl0ivr5g">
            {option.note}
            {option.chosen && <CheckIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-277" data-mp="193wl6zkw4gk" />}
          </span>
        </motion.div>)}
      <motion.div className="mt-3 flex items-center gap-2.5 text-[13px] text-white/70" initial={reduced ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4,
      delay: 0.75
    }} data-id="element-278" data-mp="fyjl0gbmzxt3">
        <UserCheckIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-279" data-mp="3emyw66rc5f93" />
        Reviewed and owned by a named person, with the reasoning recorded
      </motion.div>
    </div>;
}
function EndVisual() {
  const reduced = useReducedMotion();
  return <div className="flex h-full w-full flex-col items-center justify-center text-center" data-id="element-280" data-mp="1yx7nv3d5868i">
      <motion.div initial={reduced ? false : {
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} data-id="element-281" data-mp="1l5jakwr5rbpy">
        <CavallaMark colour="#FFFFFF" className="h-16 w-16" data-id="element-282" data-mp="2cwh4231cn0ch" />
      </motion.div>
      <motion.p className="mt-6 font-display text-[26px] leading-snug text-white sm:text-[32px]" initial={reduced ? false : {
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.45,
      ease: EASE,
      delay: 0.15
    }} data-id="element-283" data-mp="2hv0ry8zi6m7h">
        Made for people, powered by AI
      </motion.p>
      <motion.p className="mt-4 max-w-md text-[15px] text-white/65" initial={reduced ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4,
      delay: 0.35
    }} data-id="element-284" data-mp="serkmh0orjb2">
        Four steps, run on every engagement, across market intelligence, growth and AI adoption.
      </motion.p>
    </div>;
}
interface Scene {
  id: string;
  chip: string;
  kicker: string;
  title: string;
  caption: string;
  duration: number;
  visual: React.ReactNode;
}
const SCENES: Scene[] = [{
  id: 'problem',
  chip: 'The problem',
  kicker: 'Where every engagement starts',
  title: 'The evidence exists. It just does not agree.',
  caption: 'Records sit in different systems, definitions differ by department, and the decision waits for a meeting that settles none of it.',
  duration: 5600,
  visual: <Fragments ordered={false} data-id="element-285" data-mp="1t3tt2zaddvll" />
}, {
  id: 'capture',
  chip: '01 Capture',
  kicker: 'Step one',
  title: 'We gather it into one evidence base',
  caption: 'Internal records, market sources and policy signals, pulled together with every definition agreed before anyone acts on it.',
  duration: 5600,
  visual: <Fragments ordered data-id="element-286" data-mp="2t4p8lsdqk2oq" />
}, {
  id: 'apply',
  chip: '02 Apply',
  kicker: 'Step two',
  title: 'Machine analysis, paired with human judgement',
  caption: 'We are explicit about which findings are machine assisted and how much confidence you should place in each one.',
  duration: 6200,
  visual: <ApplyVisual data-id="element-287" data-mp="382hm6ezq2h0a" />
}, {
  id: 'enable',
  chip: '03 Enable',
  kicker: 'Step three',
  title: 'We build the capability, then hand it over',
  caption: 'Models, dashboards, definitions and training go to your team, so the work continues without us.',
  duration: 5800,
  visual: <EnableVisual data-id="element-288" data-mp="3lstgmkjs4rul" />
}, {
  id: 'decide',
  chip: '04 Decide',
  kicker: 'Step four',
  title: 'We stay in the room for the decision',
  caption: 'Options framed, trade offs named, and a person accountable for the choice. A report that changes nothing has failed.',
  duration: 6200,
  visual: <DecideVisual data-id="element-289" data-mp="3myctg4x61zo" />
}, {
  id: 'close',
  chip: 'Cavalla',
  kicker: 'The ethos',
  title: 'Made for people, powered by AI',
  caption: 'That is how we work, on every engagement.',
  duration: 5000,
  visual: <EndVisual data-id="element-290" data-mp="acxq5r0zx6t7" />
}];
export function ExplainerVideo() {
  const reduced = useReducedMotion();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, {
    margin: '-25% 0px -25% 0px'
  });
  const [index, setIndex] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const elapsed = React.useRef(0);
  const scene = SCENES[index];
  const running = playing && inView && !reduced;
  React.useEffect(() => {
    if (!running) return undefined;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      elapsed.current += now - last;
      last = now;
      const value = Math.min(elapsed.current / scene.duration, 1);
      setProgress(value);
      if (value < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        elapsed.current = 0;
        setProgress(0);
        setIndex(current => (current + 1) % SCENES.length);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, index, scene.duration]);
  const goTo = (next: number) => {
    elapsed.current = 0;
    setProgress(0);
    setIndex((next + SCENES.length) % SCENES.length);
  };
  return <div ref={containerRef} data-id="element-291" data-mp="3b8gm885t1x3h">
      <div className="relative overflow-hidden border border-white/15 bg-[#0d2c53]" data-id="element-292" data-mp="36a5b7u3rxgjo">
        <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center" data-id="element-293" data-mp="3682fctfxwt71">
          <div data-id="element-294" data-mp="31d55xler0htm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-295" data-mp="2wbzbnj5fwmhz">{scene.kicker}</p>
            <AnimatePresence mode="wait" data-id="element-296" data-mp="1pvn9z83esmhn">
              <motion.div key={scene.id} initial={reduced ? false : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduced ? undefined : {
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.3,
              ease: EASE
            }} data-id="element-297" data-mp="1pwok9aclp4yb">
                <h3 className="mt-4 font-display text-[26px] leading-tight text-white sm:text-[34px]" data-id="element-298" data-mp="gyf304xobhnc">
                  {scene.title}
                </h3>
                <p className="mt-4 max-w-md text-[16px] leading-relaxed text-white/68" data-id="element-299" data-mp="7xqvlt2scfjf">{scene.caption}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative h-[280px] w-full sm:h-[320px]" data-id="element-300" data-mp="3u5gsjepfach1">
            <AnimatePresence mode="wait" data-id="element-301" data-mp="3u6rtz7veb84z">
              <motion.div key={scene.id} className="absolute inset-0" initial={reduced ? false : {
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={reduced ? undefined : {
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} data-id="element-302" data-mp="3gebzivgk8ifo">
                {scene.visual}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Player controls */}
        <div className="flex flex-wrap items-center gap-4 border-t border-white/15 px-6 py-4 sm:px-10" data-id="element-303" data-mp="2nrlq6oo2fnho">
          {reduced ? <div className="flex items-center gap-2" data-id="element-304" data-mp="31rn0euq8079t">
              <button type="button" onClick={() => goTo(index - 1)} aria-label="Previous step" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:border-gold" data-id="element-305" data-mp="31p0iq29m8bdv">
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-306" data-mp="3khuygyf2c7j5" />
              </button>
              <button type="button" onClick={() => goTo(index + 1)} aria-label="Next step" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:border-gold" data-id="element-307" data-mp="3firttf2zw3bp">
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-308" data-mp="1muk47banngw5" />
              </button>
            </div> : <div className="flex items-center gap-2" data-id="element-309" data-mp="1rsbuk0vniuy8">
              <button type="button" onClick={() => setPlaying(value => !value)} aria-label={playing ? 'Pause the explainer' : 'Play the explainer'} className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink transition-colors hover:bg-[#c19f2c]" data-id="element-310" data-mp="1rr0sq6lrnx9r">
                {playing ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-311" data-mp="18wls6cjt3wjt" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-312" data-mp="1dsavmqyy5xle" />}
              </button>
              <button type="button" onClick={() => goTo(0)} aria-label="Restart from the beginning" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:border-gold" data-id="element-313" data-mp="1iqm5v2t5ma45">
                <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-314" data-mp="dlfgpjxnkk6u" />
              </button>
            </div>}

          <ol className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-3" data-id="element-315" data-mp="8u5xp26m0f3x">
            {SCENES.map((item, itemIndex) => {
            const complete = itemIndex < index;
            const active = itemIndex === index;
            return <li key={item.id} className="min-w-[92px] flex-1" data-id="element-316" data-mp="3v38jb5huvpxp" data-mpi={itemIndex}>
                  <button type="button" onClick={() => goTo(itemIndex)} aria-current={active ? 'step' : undefined} className="group w-full text-left" data-id="element-317" data-mp="3qz920kor4n3a">
                    <span className={`block text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${active ? 'text-white' : 'text-white/45 group-hover:text-white/75'}`} data-id="element-318" data-mp="3h4h75omk0ec7">
                      {item.chip}
                    </span>
                    <span className="mt-2 block h-[3px] w-full bg-white/15" data-id="element-319" data-mp="3ehy9k6itkp2r">
                      <span className="block h-full bg-gold" style={{
                    width: complete ? '100%' : active ? `${progress * 100}%` : '0%'
                  }} data-id="element-320" data-mp="2zt26yozj849j" />
                    </span>
                  </button>
                </li>;
          })}
          </ol>
        </div>
      </div>

      <p aria-live="polite" className="sr-only" data-id="element-321" data-mp="1ao8b86oouzg">
        {`Step ${index + 1} of ${SCENES.length}. ${scene.title}. ${scene.caption}`}
      </p>
    </div>;
}